// features/SearchRecolor/index.js - Search and Recolor feature
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import UploadCard from '../../components/UploadCard';
import ImagePreview from '../../components/ImagePreview';
import { FaMagic, FaDownload, FaRedo, FaSearch, FaPalette, FaInfoCircle } from 'react-icons/fa';
import apiService from '../../services/api';
import { Spinner } from '../../components/Spinner';
import { useTranslation } from 'react-i18next';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

function SearchRecolor() {
  const { setIsLoading } = useAppContext();
  const { t } = useTranslation();
  const [image, setImage] = useState(null);
  const [searchPrompt, setSearchPrompt] = useState('');
  const [colorPrompt, setColorPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [stylePreset, setStylePreset] = useState('');
  const [resultImage, setResultImage] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Handle image upload
  const handleImageUpload = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert(t('errors.invalidFileType'));
      return;
    }
    
    // Validate file size
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert(t('errors.fileSizeTooLarge'));
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImage({
        file: file,
        src: e.target.result
      });
    };
    reader.onerror = () => {
      alert(t('errors.fileReadError'));
    };
    reader.readAsDataURL(file);
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!image) {
      return;
    }
    
    setIsProcessing(true);
    setIsLoading(true);
    
    try {
      const params = {
        searchPrompt,
        colorPrompt,
        negativePrompt,
        stylePreset
      };
      
      const result = await apiService.searchAndRecolor(image.file, params);
      
      if (result.success) {
        // Convert the output path to a full URL if it's a relative path
        let resultUrl = apiService.getFullUrl(result.outputPath);
        
        setResultImage(resultUrl);
        setShowResult(true);
      } else {
        throw new Error(result.message || t('errors.processingFailed'));
      }
    } catch (error) {
      console.error('Error processing image:', error);
      alert(t('errors.processingFailed'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };
  
  // Reset the form
  const handleReset = () => {
    setImage(null);
    setSearchPrompt('');
    setColorPrompt('');
    setNegativePrompt('');
    setStylePreset('');
    setResultImage(null);
    setShowResult(false);
  };
  
  // Download the result image
  const handleDownload = () => {
    if (!resultImage) return;
    
    const link = document.createElement('a');
    link.href = resultImage;
    link.download = 'search-recolor-result.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="search-recolor-container">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="max-w-6xl mx-auto p-4"
      >
        <motion.h1
          className="text-3xl font-bold mb-6 text-gradient-tertiary"
          variants={fadeIn}
        >
          {t('features.searchRecolor.title')}
        </motion.h1>
        
        <motion.div
          variants={fadeIn}
          className="mb-8 bg-card-translucent backdrop-blur-sm p-6 rounded-xl border border-border/50 shadow-sm"
        >
          <div className="flex items-start gap-4">
            <div className="bg-tertiary/10 p-3 rounded-full">
              <FaInfoCircle className="text-xl text-tertiary" />
            </div>
            <div>
              <h3 className="text-xl font-medium mb-2 text-tertiary">{t('common.howItWorks')}</h3>
              <p className="text-text-light">
                {t('features.searchRecolor.description')}
              </p>
            </div>
          </div>
        </motion.div>
        
        {showResult ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="result-container"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="original-image">
                <h3 className="text-xl font-medium mb-4 text-tertiary">{t('common.originalImage')}</h3>
                <ImagePreview
                  src={image.src}
                  alt={t('common.originalImage')}
                  className="w-full rounded-lg overflow-hidden"
                />
              </div>
              
              <div className="result-image">
                <h3 className="text-xl font-medium mb-4 text-tertiary">{t('common.resultImage')}</h3>
                <div className="relative">
                  {resultImage ? (
                    <img src={resultImage} alt={t('common.processedImage')} className="w-full h-auto" />
                  ) : (
                    <div className="w-full h-64 bg-gray-200 flex items-center justify-center">
                      <Spinner />
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4 justify-center mt-8">
              <Button
                variant="outline"
                onClick={handleReset}
                className="flex items-center gap-2"
              >
                <FaRedo />
                {t('common.startOver')}
              </Button>
              
              <Button
                variant="default"
                onClick={handleDownload}
                disabled={!resultImage}
                className="flex items-center gap-2"
              >
                <FaDownload />
                {t('common.download')}
              </Button>
            </div>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit}>
            <motion.div variants={fadeIn} className="mb-6 space-y-6">
              {!image ? (
                <UploadCard
                  title={t('common.uploadImage')}
                  description={t('common.uploadImageDescription')}
                  onFileAccepted={handleImageUpload}
                />
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mb-6"
                >
                  <h3 className="text-xl font-medium mb-4 text-tertiary">{t('common.uploadedImage')}</h3>
                  <div className="relative">
                    <ImagePreview
                      src={image.src}
                      alt={t('common.uploadedImage')}
                      className="w-full"
                    />
                    <div className="absolute top-2 right-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setImage(null)}
                        className="bg-background/80 backdrop-blur-sm"
                      >
                        {t('common.changeImage')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
              
              <motion.div
                variants={fadeIn}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4 text-tertiary">
                      <FaSearch className="text-lg" />
                      <label className="text-lg font-medium" htmlFor="search-prompt">
                        {t('searchRecolor.whatToFind')}
                      </label>
                    </div>
                    <input
                      type="text"
                      id="search-prompt"
                      className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-tertiary/50 focus:border-tertiary outline-none transition-all"
                      value={searchPrompt}
                      onChange={(e) => setSearchPrompt(e.target.value)}
                      placeholder={t('searchRecolor.findPlaceholder')}
                      required
                    />
                    <p className="text-xs text-text-light mt-2">{t('searchRecolor.findDescription')}</p>
                  </CardContent>
                </Card>
                
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-4 text-tertiary">
                      <FaPalette className="text-lg" />
                      <label className="text-lg font-medium" htmlFor="color-prompt">
                        {t('searchRecolor.newColor')}
                      </label>
                    </div>
                    <input
                      type="text"
                      id="color-prompt"
                      className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-tertiary/50 focus:border-tertiary outline-none transition-all"
                      value={colorPrompt}
                      onChange={(e) => setColorPrompt(e.target.value)}
                      placeholder={t('searchRecolor.colorPlaceholder')}
                      required
                    />
                    <p className="text-xs text-text-light mt-2">{t('searchRecolor.colorDescription')}</p>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div variants={fadeIn} className="mt-6">
                <Card variant="glass" className="overflow-hidden">
                  <CardContent className="p-6">
                    <details className="cursor-pointer">
                      <summary className="text-lg font-medium mb-4 flex items-center gap-2 text-tertiary">
                        <FaMagic className="text-lg" />
                        {t('common.advancedOptions')}
                      </summary>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                        <div>
                          <label className="text-sm font-medium block mb-2" htmlFor="negative-prompt">
                            {t('common.negativePrompt')}
                          </label>
                          <textarea
                            id="negative-prompt"
                            className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-tertiary/50 focus:border-tertiary outline-none transition-all min-h-[80px] resize-y"
                            value={negativePrompt}
                            onChange={(e) => setNegativePrompt(e.target.value)}
                            placeholder={t('common.negativePromptPlaceholder')}
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium block mb-2" htmlFor="style-preset">
                            {t('common.stylePreset')}
                          </label>
                          <select
                            id="style-preset"
                            className="w-full p-3 rounded-lg border focus:ring-2 focus:ring-tertiary/50 focus:border-tertiary outline-none transition-all"
                            value={stylePreset}
                            onChange={(e) => setStylePreset(e.target.value)}
                          >
                            <option value="">{t('common.noPreset')}</option>
                            <option value="photographic">{t('common.stylePresets.photographic')}</option>
                            <option value="digital-art">{t('common.stylePresets.digitalArt')}</option>
                            <option value="cinematic">{t('common.stylePresets.cinematic')}</option>
                            <option value="3d-model">{t('common.stylePresets.3dModel')}</option>
                            <option value="pixel-art">{t('common.stylePresets.pixelArt')}</option>
                            <option value="isometric">{t('common.stylePresets.isometric')}</option>
                            <option value="low-poly">{t('common.stylePresets.lowPoly')}</option>
                            <option value="origami">{t('common.stylePresets.origami')}</option>
                            <option value="line-art">{t('common.stylePresets.lineArt')}</option>
                            <option value="sketch">{t('common.stylePresets.sketch')}</option>
                            <option value="watercolor">{t('common.stylePresets.watercolor')}</option>
                            <option value="oil-painting">{t('common.stylePresets.oilPainting')}</option>
                          </select>
                        </div>
                      </div>
                    </details>
                  </CardContent>
                </Card>
              </motion.div>
              
              <motion.div
                variants={fadeIn}
                className="mt-8 flex justify-center"
              >
                <Button
                  type="submit"
                  disabled={!image || !searchPrompt || !colorPrompt || isProcessing}
                  className="px-8 py-6 text-lg flex items-center gap-2"
                  variant="default"
                >
                  {isProcessing ? (
                    <>
                      <Spinner size="sm" />
                      {t('common.processing')}
                    </>
                  ) : (
                    <>
                      <FaPalette />
                      {t('searchRecolor.recolorObject')}
                    </>
                  )}
                </Button>
              </motion.div>
            </motion.div>
          </form>
        )}
        
        <motion.div
          variants={fadeIn}
          className="mt-12 p-6 rounded-xl border border-border/50 bg-card-translucent backdrop-blur-sm"
        >
          <h3 className="text-xl font-medium mb-4 text-tertiary">{t('common.tips')}</h3>
          <ul className="list-disc pl-6 space-y-2 text-text-light">
            <li>{t('searchRecolor.tip1')}</li>
            <li>{t('searchRecolor.tip2')}</li>
            <li>{t('searchRecolor.tip3')}</li>
          </ul>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default SearchRecolor;
